import { getUserName } from 'lib/user';
import { createSelector } from 'reselect';
import { errorsSelector } from 'store/error/selectors';
import { getIsWifiConnection } from 'store/network/selectors';
import {
  activeGrantorsSelector,
  consumersSelector,
  profileConsumerSelector,
  profileGrantorsSelector
} from 'store/profile/selectors';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { Color } from 'modules/styles/colors';
import { GlobalSearchItem } from 'store/global/types';

export const accountStateSelector = (state: RootState) => state?.account;

export const accountLoadingSelector = createSelector(
  accountStateSelector,
  account => !!account?.loading
);

export const accountProxySelector = createSelector(
  accountStateSelector,
  account => account?.proxiedAccount
);

export const isAccountSwitcherModalOpenSelector = createSelector(
  accountStateSelector,
  account => account.isAccountSwitcherModalOpen
);

export const currentAccountConsumerSelector = createSelector(
  [accountProxySelector, profileConsumerSelector],
  (proxiedAccount, profileConsumer) => {
    if (proxiedAccount && proxiedAccount.consumer) {
      return proxiedAccount.consumer;
    }

    return profileConsumer;
  }
);

export const isSsnExistSelector = createSelector(profileConsumerSelector, consumer => {
  let ssn = oc(consumer).ssn();

  if (ssn === '*** ** undefined') {
    ssn = undefined;
    return { ssn, isSsnExist: false };
  }
  return { ssn, isSsnExist: true };
});

export const isAdultProxyUser = createSelector(
  [accountProxySelector, profileConsumerSelector],
  (proxiedAccount, profileConsumer) => {
    const proxyConsumerId = proxiedAccount?.consumer?.consumerId;
    const profileConsumerId = profileConsumer?.consumerId;

    if (proxyConsumerId !== profileConsumerId) {
      return proxiedAccount?.consumer?.age ? proxiedAccount?.consumer?.age >= 18 : false;
    }

    return false;
  }
);

export const currentAccountConsumerIdSelector = createSelector(
  [currentAccountConsumerSelector],
  consumer => consumer?.consumerId
);

export const currentAccountConsumerAgeSelector = createSelector(
  [currentAccountConsumerSelector],
  consumer => consumer.age
);

export const currentAccountConsumerFullNameSelector = createSelector(
  [currentAccountConsumerSelector],
  consumer => getUserName(consumer)
);

/**
 * Selects accessible accounts without current consumer
 */
export const otherAccountConsumersSelector = createSelector(
  [currentAccountConsumerSelector, consumersSelector],
  (currentConsumer, allConsumers) => {
    return allConsumers.filter(c => c.consumerId !== currentConsumer.consumerId);
  }
);

/**
 * Selects active accounts without current consumer
 * Use Cases: Switch accounts & Individuals I have Access to,
 * for lists of available users excluding the current user
 */
export const otherActiveAccountsSelector = createSelector(
  otherAccountConsumersSelector,
  accounts => {
    return accounts.filter(
      a => a.isInMillenium || a.isInMillenium === undefined // Main account is undefined
    );
  }
);

export const isImpersonatingSelector = createSelector(
  [currentAccountConsumerSelector, profileConsumerSelector],
  (currentAccount, profileConsumer) => {
    return currentAccount?.consumerId !== profileConsumer?.consumerId;
  }
);

export const otherAccountConsumersNeedActivationSelector = createSelector(
  [currentAccountConsumerSelector, consumersSelector, isImpersonatingSelector],
  (currentConsumer, allConsumers, isImpersonating) => {
    if (!isImpersonating) {
      return allConsumers.some(
        c => c?.consumerId !== currentConsumer?.consumerId && !c?.isInMillenium
      );
    }
    return false;
  }
);

export const allGrantorsNeedsActivationSelector = createSelector(
  [profileGrantorsSelector, isImpersonatingSelector],
  (grantors, isImpersonating) => {
    if (!isImpersonating) {
      return grantors.every(g => g?.isInMillenium === false);
    }
    return false;
  }
);

/**
 * Selects the main consumer and their active grantors (Ping + Fhir)
 * Use Cases: Booking & Symptom Checker,
 * for complete lists of available users
 */
export const allActiveAccountsSelector = createSelector(consumersSelector, accounts =>
  accounts.filter(
    a => a.isInMillenium || a.isInMillenium === undefined // Main account is undefined
  )
);

/**
 * Selects the current account concatenated with all other main user proxy
 * accounts (a list of active accounts with the current account first)
 */
export const accountSwitcherUsersSelector = createSelector(
  [currentAccountConsumerSelector, otherActiveAccountsSelector],
  (currentAccount, otherConsumers) => [currentAccount, ...otherConsumers]
);

/**
 * If proxy user: Selects the current proxy account
 * else: Selects all active accounts.
 * Use Cases: Hide other users from lists when acting as proxy
 */
export const currentConsumerAndGrantorsSelector = createSelector(
  [currentAccountConsumerSelector, activeGrantorsSelector],
  (currentAccount, profileGrantors) => {
    return profileGrantors.some(grantor => grantor.consumerId === currentAccount.consumerId)
      ? [currentAccount]
      : [currentAccount, ...profileGrantors];
  }
);

export const resetAccountAccessStartedSelector = createSelector(
  accountStateSelector,
  account => account.isAccountResetting
);

export const currentDemographicsSelector = createSelector(
  [
    currentAccountConsumerFullNameSelector,
    currentAccountConsumerIdSelector,
    getIsWifiConnection,
    errorsSelector
  ],
  (userName, userId, wifiConnection, errors) => ({
    userName,
    userId,
    wifiConnection,
    errors
  })
);

export const searchFormattedAccount: GlobalSearchItem[] = [
  {
    title: 'Account Management',
    keywords: ['account', 'profile', 'settings'],
    navWebPath: `/u/manage-account`,
    icon: 'Settings',
    weight: 1
  },
  {
    title: 'Account Profile',
    keywords: ['account', 'profile', 'personal'],
    category: `Account Management > Personal Information`,
    navWebPath: `/u/manage-account`,
    icon: 'PersonalInfo',
    weight: 0.9
  },
  {
    title: 'Edit Home Address',
    keywords: ['account', 'profile', 'personal', 'change'],
    category: 'Personal Information > Account Profile',
    navWebPath: `/u/manage-account/edit-address`,
    icon: 'Home',
    iconProps: {
      set: 'material',
      name: 'home',
      color: Color.primary
    },
    weight: 0.8
  },
  {
    title: 'Edit Phone Number',
    keywords: ['account', 'profile', 'personal', 'change'],
    category: 'Personal Information > Account Profile',
    navWebPath: `/u/manage-account/edit-phone`,
    icon: 'Phone',
    iconProps: {
      set: 'material',
      name: 'phone',
      color: Color.primary
    },
    weight: 0.7
  },
  {
    title: 'Edit Email Address',
    keywords: ['account', 'profile', 'personal', 'change'],
    category: 'Account Management > Personal Information > Account Profile',
    navWebPath: `/u/manage-account/edit-email`,
    icon: 'Mail',
    iconProps: {
      set: 'material',
      name: 'mail',
      color: Color.primary
    },
    weight: 0.6
  },
  {
    title: 'Change Password',
    keywords: ['account', 'profile', 'personal', 'change'],
    category: 'Personal Information > Account Profile',
    navWebPath: '/u/manage-account',
    icon: 'lock',
    iconProps: {
      set: 'material',
      name: 'lock',
      color: Color.primary
    },
    weight: 0.5
  },
  {
    title: 'My Insurance',
    keywords: ['account', 'profile'],
    category: 'Account Management >	Personal Information',
    navWebPath: `/u/manage-account`,
    icon: 'PersonalInfo',
    weight: 0.4
  },
  {
    title: 'Add Provider',
    keywords: ['find a doctor'],
    category: 'Personal Information > My Doctors',
    navWebPath: `/u/find-a-doctor`,
    icon: 'MyDoctors',
    weight: 0.4
  },
  {
    title: 'Notification Settings',
    keywords: ['email', 'sms', 'text', 'message'],
    category: 'Account Management > Settings',
    navWebPath: `/u/manage-account`,
    icon: 'Settings',
    weight: 0.9
  },
  {
    title: 'Account Sharing',
    keywords: ['account', 'share', 'grantors', 'grantees'],
    category: 'Account Management',
    navWebPath: `/u/manage-account`,
    icon: 'AccountShare',
    weight: 0.9
  },
  {
    title: 'Request Access to Another Account',
    keywords: ['share', 'grantors', 'grantees', 'minor'],
    category: 'Account Sharing',
    navWebPath: `/u/add-account`,
    icon: 'AccountShare',
    weight: 0.8
  },
  {
    title: 'Security and Legal',
    keywords: [],
    category: 'Account Management',
    navWebPath: `/u/manage-account`,
    icon: 'Security',
    weight: 1
  },
  {
    title: 'Terms of Use',
    keywords: [],
    category: 'Account Management > Security and Legal',
    navWebPath: `/u/manage-account`,
    icon: 'Security',
    weight: 0.5
  },
  {
    title: 'Privacy Notices',
    keywords: [],
    category: 'Account Management > Security and Legal',
    navWebPath: `/u/manage-account`,
    icon: 'Security',
    weight: 0.4
  },
  {
    title: 'HIPAA Documents',
    keywords: [],
    category: 'Account Management > Security and Legal',
    navWebPath: `/u/manage-account`,
    icon: 'Security',
    weight: 0.3
  },
  {
    title: 'Glossary of Billing Terms',
    keywords: ['define'],
    category: 'Account Management > Security and Legal',
    navWebPath: `/u/manage-account`,
    icon: 'Security',
    weight: 0.3
  },
  {
    title: 'Audit Logs',
    keywords: [],
    category: 'Account Management > Security and Legal',
    navWebPath: `/u/manage-account/audit-logs`,
    icon: 'Security',
    weight: 0.2
  }
];
